import React, { useState } from "react";
import { supabase } from "../../../services/supabaseClient";

type SearchProductsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelectProduct: (product: any) => void;
};

function SearchProductsModal({
  isOpen,
  onClose,
  onSelectProduct,
}: SearchProductsModalProps) {
  const [results, setResults] = useState(() => []);
  const [name, setName] = useState("");
  const [upc, setUpc] = useState("");
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSearch = async () => {
    setLoading(true);
    try {
      // ✅ Query products table (user-owned only)
      let query = supabase
        .from("products")
        .select("id, name, upc, certificate_type, status")
        .order("name");

      if (name.trim()) {
        query = query.ilike("name", `%${name.trim()}%`);
      }

      if (upc.trim()) {
        query = query.ilike("upc", `%${upc.trim()}%`);
      }

      const { data, error } = await query.limit(50);

      if (error) {
        console.error("Products search error:", error);
        setResults([]);
      } else {
        setResults(data || []);
      }
    } catch (err) {
      console.error("Unexpected search error:", err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="data-modal-overlay" onClick={onClose}>
      <div className="data-modal" onClick={(e) => e.stopPropagation()}>
        <div className="data-modal-header">
          <h2 className="data-modal-title">Search Products</h2>
          <button className="modal-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className="data-modal-body">
          <div className="form-grid">
            {/* Product Name */}
            <div className="form-group">
              <label className="form-label">Product Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search by product name..."
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {/* UPC */}
            <div className="form-group">
              <label className="form-label">UPC Code</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search by UPC code..."
                value={upc}
                onChange={(e) => setUpc(e.target.value)}
              />
            </div>

            {/* Search Button */}
            <div className="form-group">
              <button
                className="modal-btn modal-btn-save"
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? "Searching..." : "Search"}
              </button>
            </div>
          </div>

          {/* Instruction Line */}
          <div
            style={{
              fontSize: "0.9em",
              margin: "10px 0 6px 0",
              color: "#444",
            }}
          >
            Click <strong>Use This Product</strong> to select and confirm a
            product that applies to this test report.
          </div>

          {/* Results */}
          <div
            className="results-list"
            style={{
              maxHeight: "250px",
              overflowY: "auto",
              marginTop: "5px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              padding: "8px",
            }}
          >
            {results.length === 0 ? (
              <div>No products found</div>
            ) : (
              results.map((product: any) => (
                <div
                  key={`product-${product.id}`}
                  className="product-result"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    padding: "8px 0",
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <div>
                    <strong>{product.name}</strong>
                    <div style={{ fontSize: "0.9em", color: "#555" }}>
                      UPC: {product.upc || "N/A"} <br />
                      Type: {product.certificate_type} —{" "}
                      {product.status.charAt(0).toUpperCase() +
                        product.status.slice(1)}
                    </div>
                  </div>

                  {/* Use This Product Button */}
                  <button
                    className="modal-btn modal-btn-save"
                    style={{
                      alignSelf: "flex-start",
                      fontSize: "0.8em",
                      padding: "4px 10px",
                      marginTop: "2px",
                    }}
                    onClick={() => {
                      onSelectProduct(product);
                      onClose();
                    }}
                  >
                    Use This Product
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="data-modal-footer">
          <button className="modal-btn modal-btn-cancel" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default SearchProductsModal;
